import React, { useState, useEffect } from 'react';
import Topbar from '../components/Topbar';
import Modal from '../components/Modal';
import api, { API } from '../utils/api';
import { MagnifyingGlass, PencilSimple, Trash, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import useContentHeight from '../hooks/useContentHeight';

const Proveedores = () => {
  const contentHeight = useContentHeight(280);
  const [proveedores, setProveedores] = useState([]);
  const [filteredProveedores, setFilteredProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('todos');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState(null);
  const [useNewTipo, setUseNewTipo] = useState(false);
  const [useNewPeriodo, setUseNewPeriodo] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    tipo: 'Hosting',
    periodo: 'mensual',
    fecha_registro: '',
    fecha_vencimiento: '',
    precio: '',
    estado: ''
  });

  useEffect(() => {
    fetchProveedores();
  }, []);

  useEffect(() => {
    filterProveedores();
  }, [proveedores, searchTerm, filter]);

  const fetchProveedores = async () => {
    try {
      const response = await api.get(`${API}/proveedores`);
      setProveedores(response.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  const filterProveedores = () => {
    let filtered = [...proveedores];

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.tipo.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filter === 'pagados') {
      filtered = filtered.filter(p => p.estado === 'pagado');
    } else if (filter === 'por_vencer') {
      filtered = filtered.filter(p => p.estado === 'por vencer');
    } else if (filter === 'vencidos') {
      filtered = filtered.filter(p => p.estado === 'vencido');
    } else if (filter === 'todos') {
      // Excluir pagados en vista todos
      filtered = filtered.filter(p => p.estado !== 'pagado' && p.estado !== 'inactivo');
    }

    setFilteredProveedores(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        precio: parseFloat(formData.precio) || 0
      };

      if (editingProveedor) {
        await api.put(`${API}/proveedores/${editingProveedor.id}`, data);
        toast.success('Proveedor actualizado');
      } else {
        await api.post(`${API}/proveedores`, data);
        toast.success('Proveedor creado');
      }
      
      setIsModalOpen(false);
      resetForm();
      fetchProveedores();
    } catch (error) {
      toast.error('Error al guardar proveedor');
    }
  };

  const handleEdit = (proveedor) => {
    setEditingProveedor(proveedor);
    setFormData({
      nombre: proveedor.nombre,
      tipo: proveedor.tipo,
      periodo: (proveedor.periodo || '').trim(),
      fecha_registro: proveedor.fecha_registro,
      fecha_vencimiento: proveedor.fecha_vencimiento || '',
      precio: proveedor.precio.toString(),
      estado: proveedor.estado
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este proveedor?')) return;
    try {
      await api.delete(`${API}/proveedores/${id}`);
      toast.success('Proveedor eliminado');
      fetchProveedores();
    } catch (error) {
      toast.error('Error al eliminar proveedor');
    }
  };

  const resetForm = () => {
    setEditingProveedor(null);
    setUseNewTipo(false);
    setUseNewPeriodo(false);
    setFormData({
      nombre: '',
      tipo: 'Hosting',
      periodo: 'mensual',
      fecha_registro: '',
      precio: '',
      estado: ''
    });
  };

  const openNewModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  // Obtener tipos únicos de proveedores existentes
  const tiposExistentes = [...new Set(proveedores.map(p => p.tipo))].sort();
  const periodosExistentes = ['mensual', 'semestral', 'anual'];

  const getEstadoBadge = (estado) => {
    const styles = {
      'activo': 'bg-[#22c55e]/10 border-[#22c55e] text-[#22c55e]',
      'por vencer': 'bg-[#f59e0b]/10 border-[#f59e0b] text-[#f59e0b]',
      'vencido': 'bg-[#ef4444]/10 border-[#ef4444] text-[#ef4444]',
      'pagado': 'bg-[#4f9eff]/10 border-[#4f9eff] text-[#4f9eff]'
    };
    return styles[estado] || styles['activo'];
  };

  return (
    <div className="flex-1 overflow-y-auto bg-[#0d0f14] min-h-screen">
      <Topbar />
      
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-white font-['Fraunces'] text-2xl md:text-3xl font-bold" data-testid="page-title">Egresos</h1>
            <p className="text-[#8b949e] font-['Mulish'] text-sm mt-1">Visionweb / Módulos / Egresos</p>
          </div>
          <button
            onClick={openNewModal}
            className="flex items-center justify-center gap-2 px-3 md:px-4 py-2 bg-[#4f9eff] text-[#0d0f14] rounded-sm font-['IBM_Plex_Mono'] text-sm font-bold hover:bg-[#4f9eff]/90 transition-colors"
            data-testid="new-proveedor-btn"
          >
            <Plus size={18} />
            <span className="hidden md:inline">Nuevo Egreso</span>
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b949e]" size={18} />
            <input
              type="text"
              placeholder="Buscar proveedor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#13161e] border border-[#2a2e3d] rounded-sm text-white font-['Mulish'] text-sm focus:outline-none focus:border-[#4f9eff]"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
            {['todos', 'pagados', 'por_vencer', 'vencidos'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 md:px-4 py-2 rounded-sm font-['IBM_Plex_Mono'] text-xs whitespace-nowrap transition-colors ${
                  filter === f
                    ? 'bg-[#4f9eff] text-[#0d0f14] font-bold'
                    : 'bg-[#13161e] border border-[#2a2e3d] text-[#8b949e] hover:text-white'
                }`}
                data-testid={`filter-${f}`}
              >
                {f === 'todos' ? 'Todos' : f === 'pagados' ? 'Pagados' : f === 'por_vencer' ? 'Por vencer' : 'Vencidos'}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile Cards View */}
        <div className="block md:hidden overflow-y-auto space-y-3 pr-1 pb-28" style={{ maxHeight: contentHeight }}>
          {filteredProveedores.map((proveedor) => (
            <div key={proveedor.id} className="bg-[#13161e] border border-[#2a2e3d] rounded-md p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-white font-['IBM_Plex_Mono'] text-sm font-bold">{proveedor.nombre}</h3>
                  <p className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs">{proveedor.tipo}</p>
                </div>
                <span className={`px-2 py-1 rounded-sm border text-xs font-['IBM_Plex_Mono'] ${getEstadoBadge(proveedor.estado)}`}>
                  {proveedor.estado}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <div>
                  <span className="text-[#8b949e]">Periodo:</span>
                  <span className="text-white ml-1 capitalize">{proveedor.periodo}</span>
                </div>
                <div>
                  <span className="text-[#8b949e]">Precio:</span>
                  <span className="text-[#22c55e] ml-1 font-['IBM_Plex_Mono']">${proveedor.precio.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-[#8b949e]">Vencimiento:</span>
                  <span className="text-white ml-1 font-['IBM_Plex_Mono']">{proveedor.fecha_vencimiento}</span>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => handleEdit(proveedor)} className="text-[#4f9eff] hover:text-white transition-colors p-1">
                  <PencilSimple size={18} />
                </button>
                <button onClick={() => handleDelete(proveedor.id)} className="text-[#ef4444] hover:text-white transition-colors p-1">
                  <Trash size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block bg-[#13161e] border border-[#2a2e3d] rounded-md overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>
            <table className="w-full">
              <thead className="bg-[#0d0f14] border-b border-[#2a2e3d] sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider">Concepto</th>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider">Periodo</th>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider">Precio</th>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider">Vencimiento</th>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2a2e3d]">
                {filteredProveedores.map((proveedor) => (
                  <tr key={proveedor.id} className="hover:bg-[#1a1e28] transition-colors group">
                    <td className="px-6 py-4">
                      <div className="text-white font-['IBM_Plex_Mono'] text-sm font-semibold">{proveedor.nombre}</div>
                      <div className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs">{proveedor.tipo}</div>
                    </td>
                    <td className="px-6 py-4 text-[#8b949e] font-['IBM_Plex_Mono'] text-sm capitalize">{proveedor.periodo}</td>
                    <td className="px-6 py-4 text-[#22c55e] font-['IBM_Plex_Mono'] text-sm font-bold">${proveedor.precio.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-6 py-4 text-white font-['IBM_Plex_Mono'] text-sm">{proveedor.fecha_vencimiento}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-sm border text-xs font-['IBM_Plex_Mono'] ${getEstadoBadge(proveedor.estado)}`}>
                        {proveedor.estado}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(proveedor)}
                          className="text-[#4f9eff] hover:text-white transition-colors"
                        >
                          <PencilSimple size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(proveedor.id)}
                          className="text-[#ef4444] hover:text-white transition-colors"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty state */}
        {!loading && filteredProveedores.length === 0 && (
          <div className="text-center py-12">
            <p className="text-[#8b949e] font-['Mulish']">No se encontraron proveedores</p>
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingProveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Proveedor */}
          <div>
            <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Proveedor *</label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="w-full px-3 py-2 bg-[#0d0f14] border border-[#2a2e3d] rounded-sm text-white font-['Mulish'] text-sm focus:outline-none focus:border-[#4f9eff]"
              required
              data-testid="proveedor-nombre"
            />
          </div>

          {/* Tipo | Periodo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tipo */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase">Tipo *</label>
                <button
                  type="button"
                  onClick={() => {
                    setUseNewTipo(!useNewTipo);
                    if (!useNewTipo) setFormData({ ...formData, tipo: '' });
                  }}
                  className="text-[#4f9eff] font-['IBM_Plex_Mono'] text-xs hover:underline"
                >
                  {useNewTipo ? 'Seleccionar existente' : 'Agregar nuevo'}
                </button>
              </div>
              {useNewTipo ? (
                <input
                  type="text"
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0d0f14] border border-[#2a2e3d] rounded-sm text-white font-['Mulish'] text-sm focus:outline-none focus:border-[#4f9eff]"
                  placeholder="Escribir nuevo tipo..."
                  required
                  data-testid="proveedor-tipo-input"
                />
              ) : (
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0d0f14] border border-[#2a2e3d] rounded-sm text-white font-['Mulish'] text-sm focus:outline-none focus:border-[#4f9eff]"
                  required
                  data-testid="proveedor-tipo"
                >
                  <option value="">Seleccionar tipo</option>
                  <option value="Hosting">Hosting</option>
                  <option value="IA">IA</option>
                  <option value="Automatización">Automatización</option>
                  <option value="Software">Software</option>
                  <option value="Cloud">Cloud</option>
                  {tiposExistentes.filter(t => !['Hosting', 'IA', 'Automatización', 'Software', 'Cloud'].includes(t)).map(tipo => (
                    <option key={tipo} value={tipo}>{tipo}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Periodo */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase">Periodo *</label>
                <button
                  type="button"
                  onClick={() => {
                    setUseNewPeriodo(!useNewPeriodo);
                    if (!useNewPeriodo) setFormData({ ...formData, periodo: '' });
                  }}
                  className="text-[#4f9eff] font-['IBM_Plex_Mono'] text-xs hover:underline"
                >
                  {useNewPeriodo ? 'Seleccionar existente' : 'Agregar nuevo'}
                </button>
              </div>
              {useNewPeriodo ? (
                <input
                  type="text"
                  value={formData.periodo}
                  onChange={(e) => setFormData({ ...formData, periodo: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0d0f14] border border-[#2a2e3d] rounded-sm text-white font-['Mulish'] text-sm focus:outline-none focus:border-[#4f9eff]"
                  placeholder="Escribir nuevo periodo..."
                  required
                  data-testid="proveedor-periodo-input"
                />
              ) : (
                <select
                  value={formData.periodo}
                  onChange={(e) => setFormData({ ...formData, periodo: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0d0f14] border border-[#2a2e3d] rounded-sm text-white font-['Mulish'] text-sm focus:outline-none focus:border-[#4f9eff]"
                  required
                  data-testid="proveedor-periodo"
                >
                  <option value="">Seleccionar periodo</option>
                  <option value="mensual">Mensual</option>
                  <option value="semestral">Semestral</option>
                  <option value="anual">Anual</option>
                  <option value="Pago Único">Pago Único</option>
                </select>
              )}
            </div>
          </div>

          {/* Fecha Registro | Fecha Vencimiento */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Fecha Registro *</label>
              <input
                type="date"
                value={formData.fecha_registro}
                onChange={(e) => setFormData({ ...formData, fecha_registro: e.target.value })}
                className="w-full px-3 py-2 bg-[#0d0f14] border border-[#2a2e3d] rounded-sm text-white font-['Mulish'] text-sm focus:outline-none focus:border-[#4f9eff]"
                required
                data-testid="proveedor-fecha"
              />
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Fecha Vencimiento</label>
              <input
                type="date"
                value={formData.fecha_vencimiento || ''}
                onChange={(e) => setFormData({ ...formData, fecha_vencimiento: e.target.value })}
                className="w-full px-3 py-2 bg-[#0d0f14] border border-[#2a2e3d] rounded-sm text-white font-['Mulish'] text-sm focus:outline-none focus:border-[#4f9eff]"
                data-testid="proveedor-fecha-vencimiento"
              />
            </div>
          </div>

          {/* Precio | Estado */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Precio *</label>
              <input
                type="number"
                step="0.01"
                value={formData.precio}
                onChange={(e) => setFormData({ ...formData, precio: e.target.value })}
                className="w-full px-3 py-2 bg-[#0d0f14] border border-[#2a2e3d] rounded-sm text-white font-['Mulish'] text-sm focus:outline-none focus:border-[#4f9eff]"
                required
                data-testid="proveedor-precio"
              />
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Estado</label>
              <select
                value={formData.estado || ''}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                className="w-full px-3 py-2 bg-[#0d0f14] border border-[#2a2e3d] rounded-sm text-white font-['Mulish'] text-sm focus:outline-none focus:border-[#4f9eff]"
                data-testid="proveedor-estado"
              >
                <option value="">Seleccionar estado</option>
                <option value="activo">Activo</option>
                <option value="por vencer">Por vencer</option>
                <option value="vencido">Vencido</option>
                <option value="pagado">Pagado</option>
              </select>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 border border-[#2a2e3d] text-[#8b949e] rounded-sm font-['IBM_Plex_Mono'] text-sm hover:text-white hover:border-white transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#4f9eff] text-[#0d0f14] rounded-sm font-['IBM_Plex_Mono'] text-sm font-bold hover:bg-[#4f9eff]/90 transition-colors"
              data-testid="submit-btn"
            >
              {editingProveedor ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Proveedores;
